import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';

import { AuthService } from '../../../../shared/services/auth.service';
import { ButtonComponent } from '../../../../shared/ui/button/button.component';
import { BLOG_COPY } from '../../constants/blog.constants';
import {
  BLOG_COMMENT_LIMITS,
  type BlogCommentCreatePayload,
} from '../../data/blog-comments.api-model';
import type { BlogCommentView } from '../../data/blog-comments.mappers';
import { BlogCommentsService } from '../../data/blog-comments.service';

type FieldKey = 'body' | 'author_name' | 'author_email';

/** Cooldown del botón tras un 429 si `Retry-After` no se puede leer (P40). */
export const BLOG_COMMENT_COOLDOWN_FALLBACK_S = 60;

/** `required` que además rechaza lo que solo son espacios (la API recorta). */
function notBlank(control: AbstractControl<string>): ValidationErrors | null {
  return control.value.trim() === '' ? { required: true } : null;
}

/** Segundos de `Retry-After` (entero o fecha HTTP); `null` si no se lee. */
function retryAfterSeconds(header: string | null): number | null {
  if (header === null) return null;
  const value = header.trim();
  if (/^\d+$/.test(value)) {
    const seconds = Number(value);
    return seconds > 0 ? seconds : null;
  }
  const at = Date.parse(value);
  if (Number.isNaN(at)) return null;
  const seconds = Math.ceil((at - Date.now()) / 1000);
  return seconds > 0 ? seconds : null;
}

/**
 * Formulario de comentario (B110–B113). Solo se instancia en cliente, dentro
 * del `@defer` de `app-article-comments`: por eso vive en su propio componente,
 * para que `@angular/forms` salga del chunk del artículo.
 *
 * El padre es dueño del hilo y de `replyTo`; este componente solo envía y
 * avisa: `sent` (201), `closed` (404) y `cancelReply`.
 */
@Component({
  selector: 'app-article-comment-form',
  imports: [ReactiveFormsModule, ButtonComponent],
  templateUrl: './article-comment-form.component.html',
  styleUrl: './article-comment-form.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class ArticleCommentFormComponent {
  readonly slug = input.required<string>();
  readonly replyTo = input<BlogCommentView | null>(null);
  /** Lo incrementa el padre en cada «Responder»: pide foco en el textarea. */
  readonly focusRequest = input(0);

  readonly cancelReply = output<void>();
  /**
   * 201. Lleva el `parent_id` del envío (o `null` si era raíz) para que el
   * padre solo limpie `replyTo` si sigue siendo esa raíz (H1: el lector pudo
   * elegir otra mientras el POST estaba en vuelo).
   */
  readonly sent = output<number | null>();
  readonly closed = output<void>();

  private readonly service = inject(BlogCommentsService);
  private readonly auth = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly copy = BLOG_COPY.comments;
  protected readonly limits = BLOG_COMMENT_LIMITS;

  protected readonly form = inject(NonNullableFormBuilder).group({
    body: ['', [notBlank, Validators.maxLength(BLOG_COMMENT_LIMITS.body)]],
    author_name: ['', [notBlank, Validators.maxLength(BLOG_COMMENT_LIMITS.name)]],
    author_email: [
      '',
      [Validators.required, Validators.email, Validators.maxLength(BLOG_COMMENT_LIMITS.email)],
    ],
    // Honeypot (B111): sin validación; viaja siempre tal cual.
    website: [''],
  });

  protected readonly isAuthenticated = this.auth.isAuthenticated;
  /**
   * Nombre con el que la API congela el comentario (B89, `snapshotNameOf`:
   * `name` + `surnames`). Vacío → no se pinta «Comentas como».
   */
  protected readonly userName = computed(() => {
    const user = this.auth.currentUser();
    return [user?.name, user?.surnames].filter(Boolean).join(' ').trim();
  });

  protected readonly submitting = signal(false);
  protected readonly coolingDown = signal(false);
  /** Tras el primer intento de envío se enseñan los errores de cliente aunque no haya `touched`. */
  private readonly attempted = signal(false);
  protected readonly successMessage = signal<string | null>(null);
  protected readonly generalErrors = signal<string[]>([]);
  private readonly serverErrors = signal<Partial<Record<FieldKey, string>>>({});

  private readonly bodyValue = toSignal(this.form.controls.body.valueChanges, {
    initialValue: this.form.controls.body.value,
  });
  protected readonly bodyLength = computed(() => this.bodyValue().length);
  protected readonly bodyOverLimit = computed(() => this.bodyLength() > BLOG_COMMENT_LIMITS.body);
  protected readonly submitDisabled = computed(() => this.submitting() || this.coolingDown());

  private readonly bodyInput = viewChild<ElementRef<HTMLTextAreaElement>>('bodyInput');
  private cooldownTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // Con sesión, nombre y correo salen de la cuenta (B89): ni se pintan ni se
    // validan ni se envían. La sesión puede restaurarse después de montar.
    effect(() => {
      const authenticated = this.isAuthenticated();
      for (const control of [this.form.controls.author_name, this.form.controls.author_email]) {
        if (authenticated) control.disable({ emitEvent: false });
        else control.enable({ emitEvent: false });
      }
    });

    // «Responder» (B112): el padre hace scroll al bloque y aquí se enfoca el
    // textarea, también si el formulario se monta justo por ese clic.
    effect(() => {
      const request = this.focusRequest();
      const textarea = this.bodyInput()?.nativeElement;
      if (request > 0 && textarea && untracked(this.replyTo) !== null) {
        textarea.focus({ preventScroll: true });
      }
    });

    this.destroyRef.onDestroy(() => {
      if (this.cooldownTimer !== null) clearTimeout(this.cooldownTimer);
    });
  }

  /** Mensaje bajo el campo: primero la validación de cliente, luego el 422. */
  protected fieldError(key: FieldKey): string | null {
    const control = this.form.controls[key];
    if (control.enabled && control.invalid && (control.touched || this.attempted())) {
      return this.clientMessage(key, control.errors ?? {});
    }
    return this.serverErrors()[key] ?? null;
  }

  protected clearServerError(key: FieldKey): void {
    if (!(key in this.serverErrors())) return;
    this.serverErrors.update((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
  }

  protected submit(): void {
    if (this.submitDisabled()) return;

    this.attempted.set(true);
    this.successMessage.set(null);
    this.generalErrors.set([]);
    this.serverErrors.set({});
    this.form.markAllAsTouched();
    if (this.form.invalid) return;

    const value = this.form.getRawValue();
    // El honeypot se manda tal cual: el front no decide ni avisa (B111).
    const payload: BlogCommentCreatePayload = { body: value.body, website: value.website };
    if (!this.isAuthenticated()) {
      payload.author_name = value.author_name;
      payload.author_email = value.author_email;
    }
    const parent = this.replyTo();
    if (parent) payload.parent_id = parent.id;

    // H1: el formulario sigue editable durante el POST. Se guarda qué se envió
    // para que el 201 limpie solo lo que sigue correspondiendo a ese envío.
    const snapshot = { body: value.body, parentId: parent?.id ?? null };

    this.submitting.set(true);
    this.service
      .create(this.slug(), payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (created) => {
          this.submitting.set(false);
          // Nace pendiente (B113): no entra al hilo. Nombre y correo se
          // conservan para un segundo comentario.
          this.successMessage.set(created.message ?? this.copy.sentFallback);
          // Solo se vacía el texto enviado; un borrador escrito durante la
          // espera se respeta, con su estado de validación.
          if (this.form.controls.body.value === snapshot.body) {
            this.form.controls.body.reset('');
            this.attempted.set(false);
          }
          this.sent.emit(snapshot.parentId);
        },
        error: (err: unknown) => {
          this.submitting.set(false);
          this.handleError(err);
        },
      });
  }

  private handleError(err: unknown): void {
    if (!(err instanceof HttpErrorResponse)) {
      this.generalErrors.set([this.copy.errors.generic]);
      return;
    }

    switch (err.status) {
      case 422:
        this.applyValidationErrors(err.error?.error);
        return;
      case 429: {
        // El texto de la API se pinta tal cual (contrato A3), sin copy propio.
        const message = err.error?.error;
        this.generalErrors.set([
          typeof message === 'string' && message.trim() !== '' ? message : this.copy.errors.generic,
        ]);
        const seconds =
          retryAfterSeconds(err.headers.get('Retry-After')) ?? BLOG_COMMENT_COOLDOWN_FALLBACK_S;
        this.startCooldown(seconds);
        return;
      }
      case 404:
        this.closed.emit();
        return;
      default:
        this.generalErrors.set([this.copy.errors.generic]);
    }
  }

  /** 422 con `error` objeto: primer mensaje por clave; lo desconocido, a la zona general. */
  private applyValidationErrors(errors: unknown): void {
    if (typeof errors !== 'object' || errors === null || Array.isArray(errors)) {
      this.generalErrors.set([this.copy.errors.generic]);
      return;
    }

    const byField: Partial<Record<FieldKey, string>> = {};
    const general: string[] = [];
    for (const [key, messages] of Object.entries(errors as Record<string, unknown>)) {
      const first = Array.isArray(messages) ? messages[0] : messages;
      if (typeof first !== 'string' || first.trim() === '') continue;
      if (this.isVisibleField(key)) byField[key] = first;
      else general.push(first);
    }

    this.serverErrors.set(byField);
    this.generalErrors.set(
      general.length === 0 && Object.keys(byField).length === 0
        ? [this.copy.errors.generic]
        : general,
    );
  }

  /** Nombre y correo no se pintan con sesión: sus 422 irían a un campo invisible. */
  private isVisibleField(key: string): key is FieldKey {
    if (key === 'body') return true;
    return (key === 'author_name' || key === 'author_email') && !this.isAuthenticated();
  }

  private startCooldown(seconds: number): void {
    if (this.cooldownTimer !== null) clearTimeout(this.cooldownTimer);
    this.coolingDown.set(true);
    this.cooldownTimer = setTimeout(() => {
      this.cooldownTimer = null;
      this.coolingDown.set(false);
    }, seconds * 1000);
  }

  private clientMessage(key: FieldKey, errors: ValidationErrors): string {
    const e = this.copy.errors;
    switch (key) {
      case 'body':
        return errors['required'] ? e.bodyRequired : e.bodyTooLong;
      case 'author_name':
        return errors['required'] ? e.nameRequired : e.nameTooLong;
      case 'author_email':
        // Mismo criterio que la API: «inválido o demasiado largo» es un solo mensaje.
        return errors['required'] ? e.emailRequired : e.emailInvalid;
    }
  }
}
